import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../context/ToastContext';
import API from '../api/axios';
import './FacultyCertification.css';

const ChevronDown = () => (
  <svg className="fc-select-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
  </svg>
);

const UsersIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const Pagination = ({ page, total, limit, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (total === 0) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="fc-pagination">
      <span className="fc-pagination-info">Showing {from}–{to} of {total}</span>
      <div className="fc-pagination-btns">
        <button className="fc-page-btn" disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</button>
        <span className="fc-page-current">Page {page} of {totalPages}</span>
        <button className="fc-page-btn" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Next</button>
      </div>
    </div>
  );
};

const roleLabel = (role) => {
  if (role === 'subject_matter_expert') return 'SME';
  return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const roleBadgeClass = (role) => {
  if (role === 'examiner') return 'fc-role-badge--examiner';
  if (role === 'subject_matter_expert') return 'fc-role-badge--sme';
  if (role === 'moderator') return 'fc-role-badge--moderator';
  return 'fc-role-badge--default';
};

const FacultyCertification = () => {
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState('eligible');
  const [eligibleFaculty, setEligibleFaculty] = useState([]);
  const [certifiedFaculty, setCertifiedFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colleges, setColleges] = useState([]);
  const [filters, setFilters] = useState({ role: '', college_id: '', email: '' });
  // Separate, instantly-updated input value so typing stays responsive; the
  // actual email filter (which triggers the fetch) is debounced below.
  const [emailInput, setEmailInput] = useState('');
  const PAGE_LIMIT = 10;
  const [eligiblePage, setEligiblePage] = useState(1);
  const [eligibleTotal, setEligibleTotal] = useState(0);
  const [certifiedPage, setCertifiedPage] = useState(1);
  const [certifiedTotal, setCertifiedTotal] = useState(0);

  const [showCertifyModal, setShowCertifyModal] = useState(false);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [certificationComments, setCertificationComments] = useState('');

  const [showAssignSubjectModal, setShowAssignSubjectModal] = useState(false);
  const [universitySubjects, setUniversitySubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [facultyUniversitySubjects, setFacultyUniversitySubjects] = useState([]);

  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchColleges = useCallback(async () => {
    try {
      const { data } = await API.get('/public/colleges');
      setColleges(data.colleges || []);
    } catch {}
  }, []);

  const fetchEligibleFaculty = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.college_id) params.append('college_id', filters.college_id);
      if (filters.email) params.append('email', filters.email);
      params.append('page', eligiblePage);
      params.append('limit', PAGE_LIMIT);
      const { data } = await API.get(`/faculty-certification/eligible-faculty?${params}`);
      if (data.success) {
        setEligibleFaculty(data.eligibleFaculty);
        setEligibleTotal(data.total ?? data.eligibleFaculty.length);
      }
    } catch {
      showToast('Failed to fetch eligible faculty', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, eligiblePage, showToast]);

  const fetchCertifiedFaculty = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.college_id) params.append('college_id', filters.college_id);
      if (filters.email) params.append('email', filters.email);
      params.append('page', certifiedPage);
      params.append('limit', PAGE_LIMIT);
      const { data } = await API.get(`/faculty-certification/certified-faculty?${params}`);
      if (data.success) {
        setCertifiedTotal(data.total ?? data.certifiedFaculty.length);
        const withSubjects = await Promise.all(
          data.certifiedFaculty.map(async (faculty) => {
            try {
              const res = await API.get(`/faculty-certification/examiner-university-subjects/${faculty.user_id}`);
              return { ...faculty, university_subjects: res.data.success ? (res.data.university_subjects || []) : [] };
            } catch {
              return { ...faculty, university_subjects: [] };
            }
          })
        );
        setCertifiedFaculty(withSubjects);
      }
    } catch {
      showToast('Failed to fetch certified faculty', 'error');
    }
  }, [filters, certifiedPage, showToast]);

  const fetchUniversitySubjects = useCallback(async () => {
    try {
      const { data } = await API.get('/faculty-certification/university-subjects');
      if (data.success) setUniversitySubjects(data.university_subjects || []);
    } catch {}
  }, []);

  useEffect(() => {
    fetchColleges();
    fetchUniversitySubjects();
  }, [fetchColleges, fetchUniversitySubjects]);

  useEffect(() => {
    fetchEligibleFaculty();
    fetchCertifiedFaculty();
  }, [fetchEligibleFaculty, fetchCertifiedFaculty]);

  // Reset to first page whenever the filters change.
  useEffect(() => {
    setEligiblePage(1);
    setCertifiedPage(1);
  }, [filters]);

  // Debounce the email search: only commit to filters (which triggers the
  // fetch) after the user stops typing for 400ms, so it doesn't search on
  // every keystroke / backspace.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters(f => (f.email === emailInput ? f : { ...f, email: emailInput }));
    }, 400);
    return () => clearTimeout(handle);
  }, [emailInput]);

  const fetchFacultyUniversitySubjects = async (userId) => {
    try {
      const { data } = await API.get(`/faculty-certification/examiner-university-subjects/${userId}`);
      if (data.success) setFacultyUniversitySubjects(data.university_subjects || []);
    } catch {
      showToast('Failed to fetch faculty subjects', 'error');
    }
  };

  const handleGrantCertification = async () => {
    if (!selectedFaculty || !certificationComments.trim()) {
      showToast('Please provide comments for certification', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const { data } = await API.post('/faculty-certification/grant-certification', {
        user_id: selectedFaculty.user_id,
        comments: certificationComments
      });
      if (data.success) {
        showToast(`Pool access granted to ${selectedFaculty.name}`, 'success');
        setShowCertifyModal(false);
        setSelectedFaculty(null);
        setCertificationComments('');
        fetchEligibleFaculty();
        fetchCertifiedFaculty();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to grant certification', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeCertification = async () => {
    if (!revokeTarget || !revokeReason.trim()) return;
    setActionLoading(true);
    try {
      const { data } = await API.post('/faculty-certification/revoke-certification', {
        user_id: revokeTarget.user_id,
        reason: revokeReason
      });
      if (data.success) {
        showToast(`Pool access revoked from ${revokeTarget.name}`, 'success');
        setShowRevokeModal(false);
        setRevokeTarget(null);
        setRevokeReason('');
        fetchEligibleFaculty();
        fetchCertifiedFaculty();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to revoke certification', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignUniversitySubject = async () => {
    if (!selectedFaculty || !selectedSubject) {
      showToast('Please select a subject', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const { data } = await API.post('/faculty-certification/assign-university-subject', {
        user_id: selectedFaculty.user_id,
        university_subject_id: selectedSubject
      });
      if (data.success) {
        showToast(`Subject assigned to ${selectedFaculty.name}`, 'success');
        setSelectedSubject('');
        fetchFacultyUniversitySubjects(selectedFaculty.user_id);
        fetchCertifiedFaculty();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to assign subject', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeUniversitySubject = async (userUniversitySubjectId) => {
    if (!window.confirm('Remove this subject assignment?')) return;
    try {
      const { data } = await API.delete(`/faculty-certification/revoke-university-subject/${userUniversitySubjectId}`);
      if (data.success) {
        showToast('Subject assignment removed', 'success');
        if (selectedFaculty) {
          fetchFacultyUniversitySubjects(selectedFaculty.user_id);
          fetchCertifiedFaculty();
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove subject', 'error');
    }
  };

  const openCertifyModal = (faculty) => {
    setSelectedFaculty(faculty);
    setCertificationComments('');
    setShowCertifyModal(true);
  };

  const openAssignSubjectModal = (faculty) => {
    setSelectedFaculty(faculty);
    setSelectedSubject('');
    fetchFacultyUniversitySubjects(faculty.user_id);
    setShowAssignSubjectModal(true);
  };

  const openRevokeModal = (faculty) => {
    setRevokeTarget(faculty);
    setRevokeReason('');
    setShowRevokeModal(true);
  };

  if (loading) {
    return (
      <div className="fc-loading">
        <div className="fc-spinner" />
      </div>
    );
  }

  return (
    <div className="fc-page">

      {/* Page Header */}
      <div className="fc-page-header">
        <h1>Faculty Certification</h1>
        <p>Grant university pool access to department faculty and assign subjects</p>
      </div>

      {/* Filter Bar */}
      <div className="fc-filter-bar">
        <span className="fc-filter-label">Filter by</span>

        <input
          type="text"
          className="fc-search-input"
          placeholder="Search by email…"
          value={emailInput}
          onChange={e => setEmailInput(e.target.value)}
        />

        <div className="fc-select-wrapper">
          <select
            className="fc-custom-select"
            value={filters.role}
            onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}
          >
            <option value="">All Roles</option>
            <option value="examiner">Examiner</option>
            <option value="subject_matter_expert">SME</option>
            <option value="moderator">Moderator</option>
          </select>
          <ChevronDown />
        </div>

        <div className="fc-select-wrapper">
          <select
            className="fc-custom-select"
            value={filters.college_id}
            onChange={e => setFilters(f => ({ ...f, college_id: e.target.value }))}
          >
            <option value="">All Colleges</option>
            {colleges.map(c => (
              <option key={c.college_id} value={c.college_id}>{c.college_name}</option>
            ))}
          </select>
          <ChevronDown />
        </div>
      </div>

      {/* Tab Bar */}
      <div className="fc-tab-bar">
        <button
          className={`fc-tab fc-tab--eligible ${activeTab === 'eligible' ? 'fc-tab--active' : ''}`}
          onClick={() => setActiveTab('eligible')}
        >
          Eligible for Pool Access
          <span className="fc-tab-badge">{eligibleFaculty.length}</span>
        </button>
        <button
          className={`fc-tab fc-tab--certified ${activeTab === 'certified' ? 'fc-tab--active' : ''}`}
          onClick={() => setActiveTab('certified')}
        >
          Pool Access Granted
          <span className="fc-tab-badge">{certifiedFaculty.length}</span>
        </button>
      </div>

      {/* Tab: Eligible */}
      {activeTab === 'eligible' && (
        <div>
          <div className="fc-section-info">
            <h2>Faculty Eligible for University Pool Access</h2>
            <p>Active department-level faculty who can be granted access to the university question pool</p>
          </div>

          {eligibleFaculty.length === 0 ? (
            <div className="fc-empty-state">
              <div className="fc-empty-icon"><UsersIcon /></div>
              <h3>No eligible faculty found</h3>
              <p>All qualifying faculty may already have pool access, or try adjusting your filters.</p>
            </div>
          ) : (
            <div className="fc-table-wrap" style={{ overflowX: 'auto' }}>
              <table className="table fc-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>College</th>
                    <th>Department</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleFaculty.map(faculty => (
                    <tr key={faculty.user_id}>
                      <td><strong>{faculty.name}</strong></td>
                      <td>
                        <span className={`fc-role-badge ${roleBadgeClass(faculty.role)}`}>
                          {roleLabel(faculty.role)}
                        </span>
                      </td>
                      <td>{faculty.college_name || '—'}</td>
                      <td>{faculty.department_name || '—'}</td>
                      <td>{faculty.subject_name || '—'}</td>
                      <td>
                        <span className="fc-status-pill fc-status-pill--ready">
                          <span className="fc-status-dot" />
                          Ready
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="fc-btn-grant"
                          style={{ width: 'auto', padding: '0.4rem 0.85rem', fontSize: '0.8rem' }}
                          onClick={() => openCertifyModal(faculty)}
                        >
                          Grant Pool Access
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={eligiblePage} total={eligibleTotal} limit={PAGE_LIMIT} onChange={setEligiblePage} />
        </div>
      )}

      {/* Tab: Certified */}
      {activeTab === 'certified' && (
        <div>
          <div className="fc-section-info">
            <h2>University Pool Access Granted</h2>
            <p>Faculty certified for university-level question pool contribution</p>
          </div>

          {certifiedFaculty.length === 0 ? (
            <div className="fc-empty-state">
              <div className="fc-empty-icon"><ShieldIcon /></div>
              <h3>No certified faculty yet</h3>
              <p>Grant pool access to eligible faculty from the previous tab to see them here.</p>
            </div>
          ) : (
            <div className="fc-table-wrap" style={{ overflowX: 'auto' }}>
              <table className="table fc-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>College</th>
                    <th>Subject</th>
                    <th>Assigned Pool Subjects</th>
                    <th>Since</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {certifiedFaculty.map(faculty => (
                    <tr key={faculty.user_id}>
                      <td><strong>{faculty.name}</strong></td>
                      <td>
                        <span className={`fc-role-badge ${roleBadgeClass(faculty.role)}`}>
                          {roleLabel(faculty.role)}
                        </span>
                      </td>
                      <td>{faculty.college_name || '—'}</td>
                      <td>{faculty.subject_name || '—'}</td>
                      <td>
                        {faculty.university_subjects && faculty.university_subjects.length > 0 ? (
                          <div className="fc-subject-tags" style={{ margin: 0 }}>
                            {faculty.university_subjects.map(s => (
                              <span key={s.user_university_subject_id} className="fc-subject-tag">
                                {s.subject_name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="fc-no-subjects">None</span>
                        )}
                      </td>
                      <td>
                        {faculty.certified_at
                          ? new Date(faculty.certified_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          className="fc-btn-manage"
                          style={{ width: 'auto', padding: '0.4rem 0.7rem', fontSize: '0.78rem', marginRight: '0.4rem' }}
                          onClick={() => openAssignSubjectModal(faculty)}
                        >
                          Manage Subjects
                        </button>
                        <button
                          className="fc-btn-revoke"
                          style={{ width: 'auto', padding: '0.4rem 0.7rem', fontSize: '0.78rem' }}
                          onClick={() => openRevokeModal(faculty)}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={certifiedPage} total={certifiedTotal} limit={PAGE_LIMIT} onChange={setCertifiedPage} />
        </div>
      )}

      {/* ── Grant Certification Modal ── */}
      {showCertifyModal && selectedFaculty && (
        <div className="fc-modal-overlay" onClick={() => setShowCertifyModal(false)}>
          <div className="fc-modal" onClick={e => e.stopPropagation()}>
            <div className="fc-modal-header">
              <div>
                <h3>Grant University Pool Access</h3>
              </div>
              <button className="fc-modal-close" onClick={() => setShowCertifyModal(false)}>×</button>
            </div>

            <div className="fc-modal-body">
              <div className="fc-modal-who">
                <div className="fc-avatar fc-avatar--eligible fc-avatar--lg">
                  {selectedFaculty.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="fc-modal-who-name">{selectedFaculty.name}</p>
                  <p className="fc-modal-who-sub">
                    {roleLabel(selectedFaculty.role)} · {selectedFaculty.college_name}
                  </p>
                </div>
              </div>

              <div className="fc-access-list">
                <p className="fc-access-list-title">This will allow them to</p>
                <ul>
                  <li>Contribute questions to the university-wide question pool</li>
                  <li>Work on cross-college pool papers and examinations</li>
                  <li>Be assigned university-level subjects</li>
                </ul>
              </div>

              <div className="fc-form-group">
                <label className="fc-form-label">Comments (required)</label>
                <textarea
                  className="fc-form-textarea"
                  value={certificationComments}
                  onChange={e => setCertificationComments(e.target.value)}
                  placeholder="Provide reason for granting university pool access..."
                  rows={3}
                />
              </div>
            </div>

            <div className="fc-modal-footer">
              <button className="fc-btn-secondary" onClick={() => setShowCertifyModal(false)}>Cancel</button>
              <button
                className="fc-btn-primary"
                onClick={handleGrantCertification}
                disabled={!certificationComments.trim() || actionLoading}
              >
                {actionLoading ? 'Granting...' : 'Grant Pool Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Revoke Modal ── */}
      {showRevokeModal && revokeTarget && (
        <div className="fc-modal-overlay" onClick={() => setShowRevokeModal(false)}>
          <div className="fc-modal" onClick={e => e.stopPropagation()}>
            <div className="fc-modal-header">
              <div>
                <h3>Revoke Pool Access</h3>
                <p className="fc-modal-subtitle">{revokeTarget.name}</p>
              </div>
              <button className="fc-modal-close" onClick={() => setShowRevokeModal(false)}>×</button>
            </div>

            <div className="fc-modal-body">
              <div className="fc-revoke-warning">
                Revoking pool access from <strong>{revokeTarget.name}</strong> will immediately remove
                their ability to contribute to the university question pool and all subject assignments.
              </div>
              <div className="fc-form-group">
                <label className="fc-form-label">Reason for revocation (required)</label>
                <textarea
                  className="fc-form-textarea"
                  value={revokeReason}
                  onChange={e => setRevokeReason(e.target.value)}
                  placeholder="Provide reason for revoking access..."
                  rows={3}
                />
              </div>
            </div>

            <div className="fc-modal-footer">
              <button className="fc-btn-secondary" onClick={() => setShowRevokeModal(false)}>Cancel</button>
              <button
                className="fc-btn-danger"
                onClick={handleRevokeCertification}
                disabled={!revokeReason.trim() || actionLoading}
              >
                {actionLoading ? 'Revoking...' : 'Revoke Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Subjects Modal ── */}
      {showAssignSubjectModal && selectedFaculty && (
        <div className="fc-modal-overlay" onClick={() => setShowAssignSubjectModal(false)}>
          <div className="fc-modal fc-modal--lg" onClick={e => e.stopPropagation()}>
            <div className="fc-modal-header">
              <div>
                <h3>Manage Pool Subjects</h3>
                <p className="fc-modal-subtitle">
                  {selectedFaculty.name} · {roleLabel(selectedFaculty.role)}
                </p>
              </div>
              <button className="fc-modal-close" onClick={() => setShowAssignSubjectModal(false)}>×</button>
            </div>

            <div className="fc-modal-body">
              {/* 2×2 faculty meta grid */}
              <div className="fc-meta-grid-2x2">
                {[
                  ['Name', selectedFaculty.name],
                  ['Role', roleLabel(selectedFaculty.role)],
                  ['College', selectedFaculty.college_name || '—'],
                  ['Dept. Subject', selectedFaculty.subject_name || '—'],
                ].map(([label, val]) => (
                  <div key={label}>
                    <p className="fc-meta-cell-label">{label}</p>
                    <p className="fc-meta-cell-val">{val}</p>
                  </div>
                ))}
              </div>

              {/* Assign new subject */}
              <div className="fc-assign-section">
                <p className="fc-assign-section-title">Assign New Pool Subject</p>
                <div className="fc-assign-row">
                  <div className="fc-subject-select-wrapper">
                    <select
                      className="fc-subject-select"
                      value={selectedSubject}
                      onChange={e => setSelectedSubject(e.target.value)}
                    >
                      <option value="">Select a subject…</option>
                      {universitySubjects.map(s => (
                        <option key={s.university_subject_id} value={s.university_subject_id}>
                          {s.subject_name} ({s.subject_code})
                        </option>
                      ))}
                    </select>
                    <ChevronDown />
                  </div>
                  <button
                    className="fc-btn-add"
                    onClick={handleAssignUniversitySubject}
                    disabled={!selectedSubject || actionLoading}
                  >
                    {actionLoading ? 'Adding…' : '+ Add Subject'}
                  </button>
                </div>
              </div>

              {/* Currently assigned */}
              <div className="fc-assigned-section">
                <p className="fc-assigned-title">
                  Currently Assigned
                  {facultyUniversitySubjects.length > 0 && ` (${facultyUniversitySubjects.length})`}
                </p>

                {facultyUniversitySubjects.length === 0 ? (
                  <div className="fc-empty-subjects">
                    <p>No subjects assigned yet</p>
                    <small>Use the form above to assign a subject</small>
                  </div>
                ) : (
                  <table className="fc-subjects-table">
                    <thead>
                      <tr>
                        <th className="col-subject">Subject</th>
                        <th className="col-date">Assigned</th>
                        <th className="col-action"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {facultyUniversitySubjects.map(s => (
                        <tr key={s.user_university_subject_id}>
                          <td className="col-subject">{s.subject_name}</td>
                          <td className="col-date">
                            {new Date(s.certified_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </td>
                          <td className="col-action">
                            <button
                              className="fc-btn-trash"
                              title="Remove subject"
                              onClick={() => handleRevokeUniversitySubject(s.user_university_subject_id)}
                            >
                              <TrashIcon />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="fc-modal-footer">
              <button className="fc-btn-secondary" onClick={() => setShowAssignSubjectModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyCertification;
